// Shuffle page controller; manages filter controls, result count, and shuffle submission state.
import { t } from '../../../shared/config/translations'
import type { ClientUser } from '../../../shared/types/user'
import { getSettings, updateSettings } from '../../api/settings'
import {
	type FiltersHandle,
	initFilters,
} from '../../components/filters/Filters'
import {
	type FiltersState,
	hasActiveFilters,
} from '../../components/filters/filterState'
import type { PageMountContext } from '../../router'
import { getCollectionStoreState } from '../../stores/collectionStore'
import { type PreparedShuffle, prepareShuffle, runShuffle } from './runShuffle'
import {
	applyShuffleFilterDefaultsToSettings,
	buildUserDefaultShuffleFilters,
	invalidateShuffleFacetCache,
	shuffleFilters,
	updateShuffleOptions,
} from './shuffleFilters'

type ShufflePhase = 'idle' | 'preparing' | 'animating'

export class ShufflePageController {
	private readonly formEl: HTMLFormElement | null
	private readonly submitButtonEl: HTMLButtonElement | null
	private readonly matchCountEl: HTMLElement | null
	private readonly resetFiltersButtonEl: HTMLButtonElement | null
	private readonly saveDefaultsButtonEl: HTMLButtonElement | null
	private currentCollectionRevision = 0
	private defaultFilters: FiltersState
	private filterControls: FiltersHandle | null = null
	private shufflePhase: ShufflePhase = 'idle'
	private currentShuffleCount = 0
	private isFormReady = false
	private isLoadingOptions = true
	private currentSnapshotId: string | null = null
	private isDestroyed = false
	private optionsRequestSequence = 0
	private preparationSequence = 0
	private preparationController: AbortController | null = null
	private preparedShuffleKey = ''
	private preparedShuffle: PreparedShuffle | null = null
	private preparedShuffleRequest: Promise<PreparedShuffle | null> | null =
		null
	readonly ready: Promise<void>
	private active = false
	private presentationSequence = 0

	/** Captures shuffle DOM elements and starts initialization. */
	constructor(
		private readonly rootEl: HTMLElement,
		private readonly user: ClientUser | null,
		private readonly context?: PageMountContext,
	) {
		this.defaultFilters = buildUserDefaultShuffleFilters(user)
		shuffleFilters.reset(this.defaultFilters)
		this.formEl =
			this.rootEl.querySelector<HTMLFormElement>('#shuffle-form')
		this.submitButtonEl = this.rootEl.querySelector<HTMLButtonElement>(
			'#shuffle-form button[type="submit"]',
		)
		this.matchCountEl = this.rootEl.querySelector<HTMLElement>(
			'[data-shuffle-match-count]',
		)
		this.resetFiltersButtonEl =
			this.rootEl.querySelector<HTMLButtonElement>(
				'[data-reset-shuffle-filters]',
			)
		this.saveDefaultsButtonEl =
			this.rootEl.querySelector<HTMLButtonElement>(
				'[data-save-shuffle-defaults]',
			)
		this.currentSnapshotId = context?.collection?.snapshotId ?? null
		this.currentCollectionRevision = context?.collection?.revision ?? 0

		this.handleSubmit = this.handleSubmit.bind(this)
		this.handleFiltersChange = this.handleFiltersChange.bind(this)
		this.handleResetFilters = this.handleResetFilters.bind(this)
		this.handleSaveDefaults = this.handleSaveDefaults.bind(this)
		this.ready = this.init()
	}

	/** Initializes filters, result count, and page event listeners. */
	private async init(): Promise<void> {
		this.setShuffleFormReady(false)
		let isReady = false

		try {
			if (this.isDestroyed) return
			this.filterControls = await initFilters({
				signal: this.context?.signal,
				initialState: shuffleFilters.get(),
				onChange: this.handleFiltersChange,
				options: {
					showSort: false,
					showStatus: false,
					showGenre: true,
					showStyle: true,
					showYearRange: true,
					showIncludeHidden: true,
					dimReleases: false,
				},
				rootEl: this.rootEl,
			})

			if (this.isDestroyed) return
			this.primeNextShuffle(shuffleFilters.get())
			await this.getShuffleOptions()
			if (this.isDestroyed) return
			this.addEventListeners()
			this.updateFilterActionState()
			isReady = true
		} catch (error) {
			if (this.isDestroyed) return
			console.error('Failed to initialize shuffle controls', error)
			this.setCountUnavailable()
			this.setCountLoading(false)
		} finally {
			if (!this.isDestroyed) this.setShuffleFormReady(isReady)
		}
	}

	/** Registers the shuffle page event handlers. */
	private addEventListeners(): void {
		this.formEl?.addEventListener('submit', this.handleSubmit)
		this.resetFiltersButtonEl?.addEventListener(
			'click',
			this.handleResetFilters,
		)
		this.saveDefaultsButtonEl?.addEventListener(
			'click',
			this.handleSaveDefaults,
		)
	}

	/** Stores updated filters and refreshes facets. */
	private async handleFiltersChange(state: FiltersState): Promise<void> {
		if (this.isDestroyed) return
		shuffleFilters.set(state)
		this.updateFilterActionState()
		this.primeNextShuffle(state)
		await this.getShuffleOptions()
	}

	private async handleResetFilters(): Promise<void> {
		this.setFilterActionsDisabled(true)
		await this.filterControls?.setState(this.defaultFilters)
		this.updateFilterActionState()
	}

	private async handleSaveDefaults(): Promise<void> {
		const settings = await getSettings()
		if (!settings || this.isDestroyed) return

		this.setFilterActionsDisabled(true)
		const updatedSettings = applyShuffleFilterDefaultsToSettings(
			settings,
			shuffleFilters.get(),
		)
		await updateSettings(updatedSettings)
		if (this.isDestroyed) return
		this.defaultFilters = buildUserDefaultShuffleFilters({
			...(this.user ?? { _id: '' }),
			settings: updatedSettings,
		})
		this.updateFilterActionState()
	}

	/** Handles shuffle form submission and starts the result overlay flow. */
	private async handleSubmit(event: Event): Promise<void> {
		event.preventDefault()
		if (this.isDestroyed || this.submitButtonEl?.disabled) return
		if (this.shufflePhase !== 'idle') return
		const presentation = ++this.presentationSequence
		this.setShufflePhase('preparing')

		try {
			const activeFilters = shuffleFilters.get()
			const activeFilterKey = getFilterKey(activeFilters)
			const preparedShuffle =
				await this.takePreparedShuffle(activeFilters)
			if (
				this.isDestroyed ||
				!this.active ||
				presentation !== this.presentationSequence
			)
				return
			if (
				!preparedShuffle ||
				activeFilterKey !== getFilterKey(shuffleFilters.get())
			) {
				this.setShufflePhase('idle')
				return
			}
			const didStart = runShuffle(preparedShuffle, () => {
				if (!this.isDestroyed) this.setShufflePhase('idle')
			})
			if (this.isDestroyed) return
			this.setShufflePhase(didStart ? 'animating' : 'idle')
			if (didStart) this.primeNextShuffle(shuffleFilters.get())
		} catch (err) {
			if (this.isDestroyed) return
			console.error('Failed to load shuffle sample', err)
			this.setShufflePhase('idle')
		}
	}

	show(): void {
		this.active = true
	}
	hide(): void {
		this.active = false
		this.presentationSequence++
		this.setShufflePhase('idle')
	}

	async refresh(): Promise<void> {
		await this.ready
		const { readiness } = getCollectionStoreState()
		this.currentSnapshotId = readiness.snapshotId
		this.currentCollectionRevision = readiness.revision
		invalidateShuffleFacetCache()
		this.primeNextShuffle(shuffleFilters.get(), true)
		await this.getShuffleOptions(true)
		await this.filterControls?.refreshFolders()
	}

	dispose(): void {
		this.hide()
		invalidateShuffleFacetCache()
		if (this.isDestroyed) return
		this.isDestroyed = true
		this.filterControls?.dispose()
		this.optionsRequestSequence += 1
		this.preparationSequence += 1
		this.preparationController?.abort()
		this.preparationController = null
		this.formEl?.removeEventListener('submit', this.handleSubmit)
		this.resetFiltersButtonEl?.removeEventListener(
			'click',
			this.handleResetFilters,
		)
		this.saveDefaultsButtonEl?.removeEventListener(
			'click',
			this.handleSaveDefaults,
		)
	}

	private updateFilterActionState(): void {
		const matchesDefaults = !hasActiveFilters(
			shuffleFilters.get(),
			this.defaultFilters,
		)
		this.setFilterActionsDisabled(matchesDefaults)
	}

	private setFilterActionsDisabled(disabled: boolean): void {
		if (this.resetFiltersButtonEl) {
			this.resetFiltersButtonEl.disabled = disabled
		}
		if (this.saveDefaultsButtonEl) {
			this.saveDefaultsButtonEl.disabled = disabled
		}
	}

	private primeNextShuffle(filters: FiltersState, force = false): void {
		const key = getFilterKey(filters)
		if (
			!force &&
			key === this.preparedShuffleKey &&
			(this.preparedShuffle || this.preparedShuffleRequest)
		) {
			return
		}

		this.preparationController?.abort()
		const controller = new AbortController()
		this.preparationController = controller
		const sequence = ++this.preparationSequence
		this.preparedShuffleKey = key
		this.preparedShuffle = null
		const request = prepareShuffle(filters, controller.signal).catch(
			(error) => {
				if (controller.signal.aborted) return null
				if (
					!this.isDestroyed &&
					sequence === this.preparationSequence
				) {
					console.error('Failed to prepare shuffle sample', error)
				}
				return null
			},
		)
		this.preparedShuffleRequest = request
		void request.then((prepared) => {
			if (this.isDestroyed || sequence !== this.preparationSequence)
				return
			this.preparedShuffle = prepared
			this.preparedShuffleRequest = null
			this.preparationController = null
		})
	}

	private async takePreparedShuffle(
		filters: FiltersState,
	): Promise<PreparedShuffle | null> {
		const key = getFilterKey(filters)
		if (key !== this.preparedShuffleKey)
			this.primeNextShuffle(filters, true)

		const sequence = this.preparationSequence
		const prepared =
			this.preparedShuffle ?? (await this.preparedShuffleRequest)
		if (
			this.isDestroyed ||
			sequence !== this.preparationSequence ||
			key !== this.preparedShuffleKey
		) {
			return null
		}

		this.preparedShuffle = null
		this.preparedShuffleRequest = null
		this.preparedShuffleKey = ''
		this.preparationController = null
		return prepared
	}

	/** Keeps the rendered button disabled until initial filter data is ready. */
	private setShuffleFormReady(isReady: boolean): void {
		this.isFormReady = isReady
		this.updateShuffleButtonDisabledState()
	}

	private setCountLoading(loading: boolean): void {
		this.isLoadingOptions = loading
		this.matchCountEl?.classList.toggle('is-loading', loading)
		this.matchCountEl?.setAttribute('aria-busy', String(loading))
		this.updateShuffleButtonDisabledState()
	}

	private setCountUnavailable(): void {
		this.currentShuffleCount = 0
		const valueEl = this.matchCountEl?.querySelector('span')
		if (valueEl) valueEl.textContent = '—'
	}

	/** Updates the current shuffle phase and button busy state. */
	private setShufflePhase(phase: ShufflePhase): void {
		this.shufflePhase = phase
		const preparing = phase === 'preparing'
		if (!this.submitButtonEl) return

		this.submitButtonEl.classList.toggle('is-preparing', preparing)
		this.updateShuffleButtonDisabledState()
	}

	/** Disables shuffle when preparing or when filters have no matching results. */
	private updateShuffleButtonDisabledState(): void {
		if (!this.submitButtonEl) return

		const hasNoResults = this.currentShuffleCount <= 0
		this.submitButtonEl.disabled =
			!this.isFormReady ||
			this.isLoadingOptions ||
			this.shufflePhase !== 'idle' ||
			hasNoResults
	}

	/** Refreshes shuffle facets, result count, and submit disabled state. */
	private async getShuffleOptions(propagateError = false): Promise<void> {
		const sequence = ++this.optionsRequestSequence
		this.setCountLoading(true)
		try {
			const count = await updateShuffleOptions(
				this.currentSnapshotId,
				this.currentCollectionRevision,
				this.rootEl,
				() =>
					!this.isDestroyed &&
					sequence === this.optionsRequestSequence,
			)
			if (this.isDestroyed || sequence !== this.optionsRequestSequence)
				return
			this.currentShuffleCount = count
			if (this.matchCountEl) {
				const valueEl = this.matchCountEl.querySelector('span')
				const noun =
					this.currentShuffleCount === 1
						? t('common.item')
						: t('common.items')
				if (valueEl)
					valueEl.textContent = `${this.currentShuffleCount} ${noun}`
			}
			this.updateShuffleButtonDisabledState()
		} catch (err) {
			if (this.isDestroyed || sequence !== this.optionsRequestSequence)
				return
			console.error('Failed to fetch shuffle facets', err)
			if (propagateError) throw err
			this.setCountUnavailable()
		} finally {
			if (!this.isDestroyed && sequence === this.optionsRequestSequence)
				this.setCountLoading(false)
		}
	}
}

function getFilterKey(filters: FiltersState): string {
	return JSON.stringify(filters)
}
